<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useDashboardStore } from '../../store/dashboard';
import MetricGraph from '@/products/pha/modules/dashboard/components/MetricGraph.vue';
import { useRouter } from 'vue-router';

const dashboardStore = useDashboardStore();
const router = useRouter();
const emit = defineEmits(['emit', 'removeFromMetricsList']);

const toast = useToast();
const confirm = useConfirm();

const props = defineProps({
    metric: {
        type: Object,
        required: true,
    },
    metricsAvailableList: {
        type: Array,
        required: true,
    },
    userGroupAvailableList: {
        type: Array,
        required: true,
    },
});

const metricDetails = computed(() => props.metric);

const isDatasourceMetric = computed(() => {
    return props.metric.stereotype !== 'user_group' && props.metric.stereotype !== 'special_user_group';
});

const dateRangeOptions = [
    { label: 'Last 3 months', value: 3 },
    { label: 'Last 6 months', value: 6 },
    { label: 'Last 12 months', value: 12 },
    { label: 'All time', value: 0 },
];
const selectedMonths = ref(3);

const selectedGraphSource = computed(() => {
    let type = '';
    if (props.metric.stereotype === 'user_group') {
        type = 'userGroup';
    } else if (props.metric.stereotype === 'special_user_group') {
        type = 'specialUserGroup';
    } else {
        type = 'metric';
    }

    return {
        id: props.metric.id,
        type: type,
    };
});

function editMetric(id, metricDetailsData) {
    const type = metricDetailsData?.type;
    if (type === undefined || type === 'table') {
        router.push({ name: 'UpdateDatasourceUserGroups', params: { id } });
    } else {
        router.push({ name: 'UpdateMetric', params: { id } });
    }
}

const confirmRemove = (id) => {
    confirm.require({
        header: 'Delete Confirmation',
        message: 'Are you sure you want to remove metric ?',
        icon: 'pi pi-info-circle',
        accept: () => {
            emit('removeFromMetricsList', id);
            dashboardStore.removeMetricFromDashboard(id);
        },
        reject: () => {
            toast.add({ severity: 'error', summary: 'Rejected', detail: 'Cancelled', life: 5000 });
        },
    });
};
</script>

<template>
    <div class="col-12 flex justify-content-between align-items-center gap-2 flex-wrap metric-box-header">
        <h5 class="m-0">{{ metricDetails.order + ' - ' + (metricDetails.name ?? 'Metric') }}</h5>
        <div class="flex align-items-center gap-2 flex-wrap justify-content-end">
            <Dropdown
                v-if="isDatasourceMetric"
                v-model="selectedMonths"
                :options="dateRangeOptions"
                optionLabel="label"
                optionValue="value"
                class="metric-period-dropdown"
                v-tooltip.top="'Limit assessment data used for this chart'"
            />
            <Button
                icon="pi pi-pencil"
                class="p-button-rounded"
                @click="editMetric(props.metric?.id, metricDetails)"
                v-tooltip="'Edit'"
            />
            <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-danger"
                @click="confirmRemove(props.metric?.id)"
                v-tooltip="'Remove'"
            />
        </div>
    </div>
    <div class="col-12">
        <MetricGraph
            :key="`${selectedGraphSource.id}-${selectedMonths}`"
            :graphData="selectedGraphSource"
            :default-months="selectedMonths"
            :show-date-filter="false"
        />
    </div>
</template>

<style lang="scss" scoped>
.metric-box-header {
    margin-bottom: 0.25rem;
}

.metric-period-dropdown {
    min-width: 10.5rem;
    width: 10.5rem;
}

:deep(.metric-period-dropdown .p-dropdown) {
    height: 2.5rem;
}
</style>
